import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  ClockIcon,
  TrophyIcon,
  DocumentTextIcon,
  CalendarIcon,
  BookOpenIcon,
  PhotoIcon,
  HeartIcon,
  PhoneIcon,
  ArrowRightIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import searchService, { searchCategories, quickNavigation } from '../../services/searchService';

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const modalRef = useRef(null);

  // Icon mapping
  const iconMap = {
    MagnifyingGlassIcon,
    TrophyIcon,
    DocumentTextIcon,
    CalendarIcon,
    BookOpenIcon,
    PhotoIcon,
    HeartIcon,
    PhoneIcon,
    ArrowRightIcon,
    SparklesIcon
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setRecentSearches(searchService.getRecentSearches());
    }
  }, [isOpen]);

  // Handle search
  useEffect(() => {
    const performSearch = async () => {
      if (query.trim()) {
        setIsLoading(true);
        setShowSuggestions(false);
        try {
          const searchResults = await searchService.search(query, {
            category: selectedCategory,
            limit: 20
          });
          setResults(searchResults.results.map(result => 
            searchService.formatSearchResult(result)
          ));
        } catch (error) {
          console.error('Search error:', error);
          setResults([]);
        } finally {
          setIsLoading(false);
        }
      } else {
        setResults([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(performSearch, 300);
    return () => clearTimeout(debounceTimer);
  }, [query, selectedCategory]);

  // Handle suggestions
  useEffect(() => {
    const getSuggestions = async () => {
      if (query.length >= 2) {
        const suggestionResults = await searchService.getSuggestions(query);
        setSuggestions(suggestionResults);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(getSuggestions, 200);
    return () => clearTimeout(debounceTimer);
  }, [query]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Handle search submission
  const handleSearch = (searchQuery) => {
    if (searchQuery.trim()) {
      searchService.saveRecentSearch(searchQuery.trim());
      setQuery(searchQuery);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    handleSearch(suggestion);
  };

  // Handle recent search click
  const handleRecentSearchClick = (recentQuery) => {
    handleSearch(recentQuery);
  };

  // Clear recent searches
  const handleClearRecent = () => {
    searchService.clearRecentSearches();
    setRecentSearches([]);
  };

  // Get icon component
  const getIcon = (iconName) => {
    const IconComponent = iconMap[iconName];
    return IconComponent ? <IconComponent className="h-5 w-5" /> : <MagnifyingGlassIcon className="h-5 w-5" />;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-start justify-center p-4 pt-16">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div 
          ref={modalRef}
          className="relative w-full max-w-2xl bg-white rounded-lg shadow-xl"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Search</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Search Input */}
          <div className="p-4 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch(query);
                  }
                }}
                placeholder="Search sports, news, events, and more..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Category Filters */}
            <div className="flex flex-wrap gap-2 mt-3">
              {searchCategories.map((category) => {
                const IconComponent = iconMap[category.icon];
                return (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedCategory === category.id
                        ? 'bg-blue-100 text-blue-800 border border-blue-200'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {IconComponent && <IconComponent className="h-4 w-4" />}
                    {category.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="max-h-96 overflow-y-auto">
            {/* Loading */}
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Suggestions */}
            {showSuggestions && suggestions.length > 0 && (
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <SparklesIcon className="h-4 w-4" />
                  Suggestions
                </h3>
                <div className="space-y-1">
                  {suggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Search Results */}
            {!isLoading && !showSuggestions && results.length > 0 && (
              <div className="p-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">
                  Search Results ({results.length})
                </h3>
                <div className="space-y-3">
                  {results.map((result) => (
                    <Link
                      key={`${result.type}-${result.id}`}
                      to={result.url}
                      onClick={onClose}
                      className="block p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${result.categoryColor}`}>
                              {result.category}
                            </span>
                            {result.formattedDate && (
                              <span className="text-xs text-gray-500">
                                {result.formattedDate}
                              </span>
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900 mb-1">{result.title}</h4>
                          <p className="text-sm text-gray-600">{result.description}</p>
                        </div>
                        <ArrowRightIcon className="h-4 w-4 text-gray-400 ml-3 flex-shrink-0" />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {!isLoading && !showSuggestions && query && results.length === 0 && (
              <div className="p-8 text-center">
                <MagnifyingGlassIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No results found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your search or browse our quick navigation below.</p>
              </div>
            )}

            {/* Default State - Recent Searches and Quick Navigation */}
            {!query && !isLoading && (
              <div className="p-4 space-y-6">
                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                        <ClockIcon className="h-4 w-4" />
                        Recent Searches
                      </h3>
                      <button
                        onClick={handleClearRecent}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="space-y-1">
                      {recentSearches.slice(0, 5).map((recentQuery, index) => (
                        <button
                          key={index}
                          onClick={() => handleRecentSearchClick(recentQuery)}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg flex items-center gap-3"
                        >
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          {recentQuery}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Quick Navigation */}
                <div>
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Navigation</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {quickNavigation.map((item, index) => {
                      const IconComponent = iconMap[item.icon];
                      return (
                        <Link
                          key={index}
                          to={item.url}
                          onClick={onClose}
                          className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-colors"
                        >
                          {IconComponent && <IconComponent className="h-5 w-5 text-blue-600" />}
                          <div>
                            <div className="font-medium text-gray-900">{item.name}</div>
                            <div className="text-xs text-gray-600">{item.description}</div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchModal;